import { Controller } from "@hotwired/stimulus"

// Drives one field-mapping row in the mappings editor. The function <select>
// chooses which per-function panel is visible (each panel carries a
// space-separated data-function list, so one panel can serve several functions
// that take identical args); switching it shows the matching panel client-side —
// the canonical args persist with the screen's explicit save. Also
// owns the concat panel's "add part" / "remove part" buttons, cloning a
// hidden template row and renumbering the array indices so Rails receives a
// contiguous parts[] array. The mutating controls also carry the dirty-guard
// action.
export default class extends Controller {
  static targets = [
    "panel", "dataType",
    "partsList", "partTemplate",
    "stepsList", "stepTemplate",
    "branchesList", "branchTemplate",
  ]

  connect() {
    this.togglePanels()
  }

  functionChanged() {
    this.togglePanels()
  }

  // The target field owns the row's data type — it is a property of the field,
  // not a choice — and that type decides which source columns the column picker
  // may offer. So one change writes both: the read-out (with the hidden field it
  // posts) and the picker's constraint, which narrows its list and drops a
  // choice the new type excludes. Only the `column` function filters — the other
  // functions' column references are out of scope.
  targetAttributeChanged(event) {
    const dataType = event.target.selectedOptions[0]?.dataset.dataType
    if (dataType === undefined) return

    this.dataTypeTarget.dispatchEvent(
      new CustomEvent("value-text:set", { detail: { value: dataType } }),
    )

    const picker = this.element.querySelector("[data-function='column'] [data-combobox-filter-key-value]")
    if (!picker) return

    picker.dispatchEvent(new CustomEvent("combobox:constrain", { detail: { value: dataType } }))
  }

  // Show only the panel matching the current function and disable the hidden
  // panels' fields so they don't submit (otherwise a hidden [args][column]
  // would clobber the active panel's value).
  togglePanels() {
    const fn = this.functionValue
    this.panelTargets.forEach((el) => {
      const active = (el.dataset.function ?? "").split(/\s+/).includes(fn)
      el.hidden = !active
      el.querySelectorAll("input, select, textarea").forEach((field) => {
        field.disabled = !active
      })
    })
  }

  get functionValue() {
    const select = this.element.querySelector("[data-mapping-row-function]")
    return select ? select.value : null
  }

  addPart(event) {
    event.preventDefault()
    const index = this.partsListTarget.children.length
    const html = this.partTemplateTarget.innerHTML.replaceAll("NEW_INDEX", index)
    this.partsListTarget.insertAdjacentHTML("beforeend", html)
  }

  removePart(event) {
    event.preventDefault()
    event.target.closest("[data-mapping-part]").remove()
    this.reindexParts()
  }

  // After a removal the surviving parts must reindex to 0..n so Rails parses a
  // dense parts[] array instead of a sparse hash.
  reindexParts() {
    this.reindex(this.partsListTarget, "[data-mapping-part]", "parts")
  }

  addStep(event) {
    event.preventDefault()
    const index = this.stepsListTarget.children.length
    const html = this.stepTemplateTarget.innerHTML.replaceAll("NEW_INDEX", index)
    this.stepsListTarget.insertAdjacentHTML("beforeend", html)
  }

  removeStep(event) {
    event.preventDefault()
    event.target.closest("[data-mapping-step]").remove()
    this.reindex(this.stepsListTarget, "[data-mapping-step]", "steps")
  }

  addBranch(event) {
    event.preventDefault()
    const index = this.branchesListTarget.children.length
    const html = this.branchTemplateTarget.innerHTML.replaceAll("NEW_INDEX", index)
    this.branchesListTarget.insertAdjacentHTML("beforeend", html)
  }

  removeBranch(event) {
    event.preventDefault()
    event.target.closest("[data-mapping-branch]").remove()
    this.reindex(this.branchesListTarget, "[data-mapping-branch]", "branches")
  }

  // Renumber the field names of a list's rows to a dense 0..n so Rails parses a
  // contiguous array instead of a sparse hash after a removal.
  reindex(list, rowSelector, key) {
    const pattern = new RegExp(`\\[${key}\\]\\[\\d+\\]`)
    list.querySelectorAll(rowSelector).forEach((row, i) => {
      row.querySelectorAll("[name]").forEach((field) => {
        field.name = field.name.replace(pattern, `[${key}][${i}]`)
      })
    })
  }
}
